import React, { ReactNode, useCallback, useState } from 'react';
import { ShareConfirmationModal } from '../share-confirmation-modal/share-confirmation-modal';
import { ShareModal } from '../share-modal/share-modal';
import { OrderManagementRequest } from 'core/entities';
import { RecordType } from 'features/offline-reports/order-management/components/requests-table/requests-table';
import { API } from 'API';
import { useSelector } from 'react-redux';
import { selectOfflineReportsUserAccountInfo } from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';

export type HeaderTextProps = {
    primaryText: string;
    secondaryText: string;
    tertiaryText: string;
};

export type ShareModalSharedProps = {
    headerContent: ReactNode | HeaderTextProps;
    visible: boolean;
    onCloseModal: () => void;
    contentBottomElement?: ReactNode;
    record: RecordType | OrderManagementRequest;
};

type ModalToShow = 'shareModal' | 'confirmationModal';

type UsersSharingData = {
    target: string;
    sharingList: string[];
};

export const MODAL_WIDTH = 480;
export const shareModalType = 'shareModal';
export const confirmationModalType = 'confirmationModal';
export const ShareReportModal = ({
    headerContent,
    visible,
    onCloseModal,
    record,
}: ShareModalSharedProps) => {
    const userAccounts = useSelector(selectOfflineReportsUserAccountInfo);
    const [modalToShow, setModalToShow] = useState<ModalToShow>(shareModalType);
    const [isSendingRequest, setIsSendingRequest] = useState<boolean>(false);
    const [modalSharingUsers, setModalSharingUsers] = useState<UsersSharingData>({
        target: '',
        sharingList: [],
    });
    const { id, reportName } = record;
    const { setClosedReportSharedDataValue } = useOfflineReportsActions();

    const getUsersEmailsList = useCallback(
        (sharingUsersIdList: string[], target: string) => {
            const emailList: string[] = [];

            if (sharingUsersIdList.length > 0 && target === 'user') {
                userAccounts?.members.map((member) => {
                    sharingUsersIdList.find((element) => {
                        return element === member.id && emailList.push(member.username);
                    });
                    return emailList;
                });
            } else if (target === 'account') {
                userAccounts?.members.map((member) => emailList.push(member.username));
            }

            return emailList;
        },
        [userAccounts],
    );

    const onModalClosed = () => {
        setModalToShow(shareModalType);
        setIsSendingRequest(false);
        onCloseModal();
    };

    const cancelButtonClick = () => {
        onModalClosed();
    };

    const onShareReport = async (
        sharingUsersIdList: string[],
        messageContent: string,
        sharingTarget: string,
    ) => {
        try {
            setIsSendingRequest(true);

            const target = sharingTarget;
            const sharedWith = sharingUsersIdList || [];

            await API.support.shareOfflineReportsComment(id.toString(), {
                target: target,
                users: sharingUsersIdList || [],
                email_message: messageContent,
                shared_users_emails: getUsersEmailsList(sharingUsersIdList, target),
                report_name: reportName,
            });

            await setClosedReportSharedDataValue({
                id,
                target,
                sharedWith,
            });

            setModalSharingUsers({
                target: sharingTarget,
                sharingList: sharingUsersIdList,
            });
            setIsSendingRequest(false);
            setModalToShow(confirmationModalType);
        } catch (e) {
            onModalClosed();
        }
    };

    return (
        <div>
            <ShareModal
                visible={visible && modalToShow === shareModalType}
                onCloseModal={onModalClosed}
                onCancelButtonClick={cancelButtonClick}
                onShareReport={onShareReport}
                headerContent={headerContent}
                isSendingRequest={isSendingRequest}
                record={record}
            />
            <ShareConfirmationModal
                visible={visible && modalToShow === confirmationModalType}
                onCloseModal={onModalClosed}
                modalSharingUsers={modalSharingUsers}
            />
        </div>
    );
};
